import {
  arrayOf,
  boolOf,
  fail,
  idOf,
  numberOf,
  objectOf,
  optionalBool,
  optionalNumber,
  optionalText,
  textList,
  textOf,
} from '../../../shared/api/wire'
import { decodeConnection } from '../../../shared/api/connection'
import type {
  AqsiCashierProof,
  AqsiCashierRmkSettings,
  AqsiOperationStatus,
  AqsiPosCashier,
  AqsiPosCashiers,
  AqsiPriceType,
  AqsiRegister,
  AqsiRegisterLink,
  AqsiRegisters,
  AqsiSession,
  AqsiSessionClosed,
  AqsiSessionJournal,
  AqsiShift,
  AqsiShiftReport,
  CashierLogin,
  DeviceLinkState,
  OperationNextAction,
  OperationState,
  RegisterState,
  ServerLinkState,
  ShiftOpenerSource,
} from '../model/types'

const REGISTER_STATES: readonly RegisterState[] = ['free', 'mine', 'occupied', 'opened-outside', 'unreachable', 'unavailable']
const OPERATION_STATES: readonly OperationState[] = ['Pending', 'Sent', 'Succeeded', 'Failed', 'Unknown']
const NEXT_ACTIONS: readonly OperationNextAction[] = ['None', 'Reconcile', 'ManualReconciliation']
const SERVER_LINK_STATES: readonly ServerLinkState[] = ['ok', 'error', 'unknown']
const DEVICE_LINK_STATES: readonly DeviceLinkState[] = ['online', 'offline', 'not-checked', 'unknown']
const OPENER_SOURCES: readonly ShiftOpenerSource[] = ['program', 'cabinet']

function oneOf<T extends string>(value: unknown, allowed: readonly T[], field: string): T {
  const text = textOf(value, field)
  return (allowed as readonly string[]).includes(text) ? (text as T) : fail(`${field}: неизвестное значение ${text}`)
}

/** Касса списка `pos/registers`. Поля С1 (кто открыл смену, когда занявший был на связи) аддитивные: прежняя служба их не шлёт. */
export function decodeRegister(raw: unknown, field = 'касса'): AqsiRegister {
  const item = objectOf(raw, field)
  return {
    deviceId: numberOf(item.deviceId, `${field}.deviceId`),
    name: textOf(item.name, `${field}.name`),
    serialNumber: textOf(item.serialNumber ?? '', `${field}.serialNumber`),
    state: oneOf(item.state, REGISTER_STATES, `${field}.state`),
    title: textOf(item.title, `${field}.title`),
    canStart: boolOf(item.canStart, `${field}.canStart`),
    reason: optionalText(item.reason, `${field}.reason`),
    reasonCode: optionalText(item.reasonCode, `${field}.reasonCode`),
    message: textOf(item.message ?? '', `${field}.message`),
    occupiedBy: optionalText(item.occupiedBy, `${field}.occupiedBy`),
    occupiedSince: optionalText(item.occupiedSince, `${field}.occupiedSince`),
    occupiedByMe: optionalBool(item.occupiedByMe, `${field}.occupiedByMe`) ?? false,
    occupiedLastSeenAt: optionalText(item.occupiedLastSeenAt, `${field}.occupiedLastSeenAt`),
    shiftOpen: optionalBool(item.shiftOpen, `${field}.shiftOpen`),
    shiftNumber: optionalNumber(item.shiftNumber, `${field}.shiftNumber`),
    shiftOpenedAt: optionalText(item.shiftOpenedAt, `${field}.shiftOpenedAt`),
    shiftOpenedBy: optionalText(item.shiftOpenedBy, `${field}.shiftOpenedBy`),
    shiftOpenedByMe: optionalBool(item.shiftOpenedByMe, `${field}.shiftOpenedByMe`) ?? false,
    shiftOpenedBySource:
      item.shiftOpenedBySource === null || item.shiftOpenedBySource === undefined
        ? null
        : oneOf(item.shiftOpenedBySource, OPENER_SOURCES, `${field}.shiftOpenedBySource`),
    canOpenShift: optionalBool(item.canOpenShift, `${field}.canOpenShift`) ?? false,
    openShiftBlockCode: optionalText(item.openShiftBlockCode, `${field}.openShiftBlockCode`),
    // Поля будущей версии договора: сейчас служба их не отдаёт.
    foreignSessionId: optionalText(item.sessionId, `${field}.sessionId`),
    foreignSessionVersion: optionalNumber(item.sessionVersion, `${field}.sessionVersion`),
    lastContactAt: optionalText(item.lastContactAt, `${field}.lastContactAt`),
    connection: decodeConnection(item.connection, `${field}.connection`),
  }
}

/** Связь двух уровней (`aqsi.pos.link`): кабинет AQSI (уровень SRC-HEALTH `ok/error/unknown`) и аппарат кассы. */
export function decodeRegisterLink(raw: unknown): AqsiRegisterLink {
  const data = objectOf(raw, 'связь кассы')
  return {
    deviceId: numberOf(data.deviceId, 'deviceId'),
    server: oneOf(data.server, SERVER_LINK_STATES, 'server'),
    serverCheckedAt: optionalText(data.serverCheckedAt, 'serverCheckedAt'),
    serverReason: optionalText(data.serverReason, 'serverReason'),
    device: oneOf(data.device, DEVICE_LINK_STATES, 'device'),
    checkedAt: optionalText(data.checkedAt, 'checkedAt'),
    reason: optionalText(data.reason, 'reason'),
    reasonCode: optionalText(data.reasonCode, 'reasonCode'),
    probeEnabled: optionalBool(data.probeEnabled, 'probeEnabled') ?? false,
    cached: optionalBool(data.cached, 'cached') ?? false,
    nextProbeAt: optionalText(data.nextProbeAt, 'nextProbeAt'),
    lastActivityAt: optionalText(data.lastActivityAt, 'lastActivityAt'),
    lastActivitySource: optionalText(data.lastActivitySource, 'lastActivitySource'),
  }
}

export function decodeSession(raw: unknown, field = 'сессия'): AqsiSession {
  const item = objectOf(raw, field)
  return {
    id: idOf(item.id, `${field}.id`),
    registerKey: idOf(item.registerKey, `${field}.registerKey`),
    deviceId: numberOf(item.deviceId, `${field}.deviceId`),
    deviceSerialNumber: textOf(item.deviceSerialNumber, `${field}.deviceSerialNumber`),
    userDisplayName: textOf(item.userDisplayName, `${field}.userDisplayName`),
    mine: boolOf(item.mine, `${field}.mine`),
    openedAt: textOf(item.openedAt, `${field}.openedAt`),
    lastSeenAt: textOf(item.lastSeenAt, `${field}.lastSeenAt`),
    shiftNumber: optionalNumber(item.shiftNumber, `${field}.shiftNumber`),
    shiftOpenedByMe: boolOf(item.shiftOpenedByMe, `${field}.shiftOpenedByMe`),
    adoptedOutside: boolOf(item.adoptedOutside, `${field}.adoptedOutside`),
    version: numberOf(item.version, `${field}.version`),
    cashierName: optionalText(item.cashierName, `${field}.cashierName`),
    cashierId: optionalText(item.cashierId, `${field}.cashierId`),
    thisTab: optionalBool(item.thisTab, `${field}.thisTab`) ?? true,
    priceTypeResourceId: optionalText(item.priceTypeResourceId, `${field}.priceTypeResourceId`),
    priceTypeKey: optionalText(item.priceTypeKey, `${field}.priceTypeKey`),
    priceTypeName: optionalText(item.priceTypeName, `${field}.priceTypeName`),
  }
}

/** Виды цен продажи Platform (`GET catalog/price-types`, RP-FB11): первая — «Цена кассы». */
export function decodePriceTypes(raw: unknown): readonly AqsiPriceType[] {
  const data = objectOf(raw, 'виды цен')
  return arrayOf(data.items, 'items').map((value, index) => {
    const item = objectOf(value, `items[${index}]`)
    return {
      name: textOf(item.name, `items[${index}].name`),
      sourceResourceId: optionalText(item.sourceResourceId, `items[${index}].sourceResourceId`),
      key: optionalText(item.key, `items[${index}].key`),
      observedAt: optionalText(item.observedAt, `items[${index}].observedAt`),
      prices: optionalNumber(item.prices, `items[${index}].prices`),
    }
  })
}

export function decodeOptionalSession(raw: unknown): AqsiSession | null {
  return raw === null || raw === undefined ? null : decodeSession(raw)
}

export function decodeRegisters(raw: unknown): AqsiRegisters {
  const data = objectOf(raw, 'кассы')
  return {
    shopId: textOf(data.shopId ?? '', 'shopId'),
    registers: arrayOf(data.registers, 'registers').map((item, index) => decodeRegister(item, `registers[${index}]`)),
    mine: data.mine === null || data.mine === undefined ? null : decodeSession(data.mine, 'mine'),
    readAt: textOf(data.readAt, 'readAt'),
  }
}

export function decodeSessionClosed(raw: unknown): AqsiSessionClosed {
  const data = objectOf(raw, 'закрытая сессия')
  return {
    sessionId: idOf(data.sessionId, 'sessionId'),
    closed: boolOf(data.closed, 'closed'),
    version: numberOf(data.version, 'version'),
  }
}

function decodeReport(raw: unknown): AqsiShiftReport | null {
  if (raw === null || raw === undefined) return null
  const item = objectOf(raw, 'report')
  return {
    dayNumber: numberOf(item.dayNumber, 'report.dayNumber'),
    closedAt: optionalText(item.closedAt, 'report.closedAt'),
    cashierName: textOf(item.cashierName, 'report.cashierName'),
    chequesCount: numberOf(item.chequesCount, 'report.chequesCount'),
    docsCount: numberOf(item.docsCount, 'report.docsCount'),
    fsDaysRemaining: numberOf(item.fsDaysRemaining, 'report.fsDaysRemaining'),
    unsentDocsCount: numberOf(item.unsentDocsCount, 'report.unsentDocsCount'),
    firstUnsentDocDate: optionalText(item.firstUnsentDocDate, 'report.firstUnsentDocDate'),
    incomeTotalKopecks: numberOf(item.incomeTotalKopecks, 'report.incomeTotalKopecks'),
    incomeCashKopecks: numberOf(item.incomeCashKopecks, 'report.incomeCashKopecks'),
    incomeCashlessKopecks: numberOf(item.incomeCashlessKopecks, 'report.incomeCashlessKopecks'),
    refundTotalKopecks: numberOf(item.refundTotalKopecks, 'report.refundTotalKopecks'),
    cashInCashBoxKopecks: numberOf(item.cashInCashBoxKopecks, 'report.cashInCashBoxKopecks'),
    problems: textList(item.problems ?? [], 'report.problems'),
  }
}

export function decodeShift(raw: unknown): AqsiShift {
  const data = objectOf(raw, 'смена')
  return {
    isOpen: boolOf(data.isOpen, 'isOpen'),
    number: optionalNumber(data.number, 'number'),
    openedAt: optionalText(data.openedAt, 'openedAt'),
    closedAt: optionalText(data.closedAt, 'closedAt'),
    message: textOf(data.message, 'message'),
    report: decodeReport(data.report),
    registeredInn: textOf(data.registeredInn ?? '', 'registeredInn'),
    registeredOrgName: textOf(data.registeredOrgName ?? '', 'registeredOrgName'),
  }
}

export function decodeOperationStatus(raw: unknown): AqsiOperationStatus {
  const data = objectOf(raw, 'операция')
  return {
    operationId: idOf(data.operationId, 'operationId'),
    kind: textOf(data.kind, 'kind'),
    registerKey: idOf(data.registerKey, 'registerKey'),
    sessionId: idOf(data.sessionId, 'sessionId'),
    deviceId: numberOf(data.deviceId, 'deviceId'),
    state: oneOf(data.state, OPERATION_STATES, 'state'),
    attemptState: textOf(data.attemptState, 'attemptState'),
    externalOperationId: optionalText(data.externalOperationId, 'externalOperationId'),
    providerStatus: optionalText(data.providerStatus, 'providerStatus'),
    providerMessage: optionalText(data.providerMessage, 'providerMessage'),
    note: optionalText(data.note, 'note'),
    shift: data.shift === null || data.shift === undefined ? null : decodeShift(data.shift),
    createdAt: textOf(data.createdAt, 'createdAt'),
    sentAt: optionalText(data.sentAt, 'sentAt'),
    checkedAt: optionalText(data.checkedAt, 'checkedAt'),
    resolvedAt: optionalText(data.resolvedAt, 'resolvedAt'),
    nextAction: oneOf(data.nextAction, NEXT_ACTIONS, 'nextAction'),
    version: numberOf(data.version, 'version'),
  }
}

export function decodeSessionJournal(raw: unknown): AqsiSessionJournal {
  const data = objectOf(raw, 'журнал')
  return {
    deviceId: numberOf(data.deviceId, 'deviceId'),
    events: arrayOf(data.events, 'events').map((item, index) => {
      const event = objectOf(item, `events[${index}]`)
      return {
        at: textOf(event.at, `events[${index}].at`),
        kind: textOf(event.kind, `events[${index}].kind`),
        title: textOf(event.title, `events[${index}].title`),
        actorDisplayName: textOf(event.actorDisplayName, `events[${index}].actorDisplayName`),
        note: optionalText(event.note, `events[${index}].note`),
      }
    }),
  }
}

/**
 * Вход кассира (RP-FB22 4.1). Поля `login` нет — старая служба: вход не нужен. Незнакомый вид не ошибка: клиент
 * показывает «этот источник просит вход, который клиент не умеет», а список кассиров рисует целиком.
 */
export function decodeCashierLogin(raw: unknown, field: string): CashierLogin {
  if (raw === null || raw === undefined) return { kind: 'none' }
  const login = objectOf(raw, field)
  const kind = textOf(login.kind, `${field}.kind`)
  return kind === 'none' || kind === 'pin' ? { kind } : { kind: 'unsupported', value: kind }
}

function decodePosCashier(raw: unknown, field: string): AqsiPosCashier {
  const item = objectOf(raw, field)
  return {
    cashierId: idOf(item.cashierId, `${field}.cashierId`),
    name: textOf(item.name, `${field}.name`),
    position: optionalText(item.position, `${field}.position`),
    login: decodeCashierLogin(item.login, `${field}.login`),
  }
}

/** Кассиры источника (`pos/cashiers`). ИНН служба отдаёт только при праве управления кассирами и клиенту здесь не нужен. */
export function decodePosCashiers(raw: unknown): AqsiPosCashiers {
  const data = objectOf(raw, 'кассиры')
  return {
    cashiers: arrayOf(data.cashiers, 'cashiers').map((item, index) => decodePosCashier(item, `cashiers[${index}]`)),
    readAt: textOf(data.readAt ?? '', 'readAt'),
  }
}

/** Ответ проверки кода: подтверждение со сроком. `verified: false` в успешном ответе — ошибка договора, не «неверный код». */
export function decodeCashierProof(raw: unknown): AqsiCashierProof {
  const data = objectOf(raw, 'подтверждение кассира')
  if (data.verified !== true) fail('verified — не true')
  return { proof: idOf(data.proof, 'proof'), expiresAt: textOf(data.expiresAt, 'expiresAt') }
}

export function decodeCashierRmkSettings(raw: unknown): AqsiCashierRmkSettings {
  const data = objectOf(raw, 'настройки РМК кассира')
  const version = numberOf(data.version, 'version')
  if (!Number.isSafeInteger(version) || version < 0) fail('Некорректная версия настроек РМК кассира')
  return {
    version,
    defaultRegisterId: optionalText(data.defaultRegisterId, 'defaultRegisterId'),
    defaultPriceTypeResourceId: optionalText(data.defaultPriceTypeResourceId, 'defaultPriceTypeResourceId'),
    defaultPriceTypeKey: optionalText(data.defaultPriceTypeKey, 'defaultPriceTypeKey'),
    enabled: boolOf(data.enabled, 'enabled'),
  }
}
